<script lang="ts">
  import { onMount } from 'svelte';

  export let url: string;
  export let model: string;
  export let value: string | null = '';
  export let placeholder = '';
  export let multiline = false;
  export let maxlength: number | undefined = undefined;
  export let savedText = 'Saved';

  let text = value ?? '';
  let lastSaved = text;
  let saved = false;
  let timer: ReturnType<typeof setTimeout>;
  let textarea: HTMLTextAreaElement;

  // Grows the question box to fit its text.
  function fitHeight() {
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  // Hides "Saved" while typing and saves a second after typing stops.
  function handleInput() {
    saved = false;
    fitHeight();
    clearTimeout(timer);
    timer = setTimeout(save, 1000);
  }

  let pending = Promise.resolve();

  // Saves one at a time, so an older save can't land after a newer one.
  function save() {
    clearTimeout(timer);
    pending = pending.then(send, send);
  }

  // Sends the text if it changed since the last save, then shows "Saved".
  async function send() {
    if (text === lastSaved) return;

    const sending = text;
    const response = await fetch(url, {
      method: 'PATCH',
      credentials: 'same-origin',
      // Lets the save finish if the owner leaves the page right after typing.
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({ [model]: { content: sending } }),
    });
    if (!response.ok) return;

    lastSaved = sending;
    // Only says "Saved" if the owner hasn't typed more since this save started, then hides it after a second.
    if (text === sending) {
      saved = true;
      setTimeout(() => (saved = false), 1000);
    }
  }

  onMount(fitHeight);
</script>

<div class="flex-1 min-w-0">
  {#if multiline}
    <textarea
      bind:this={textarea}
      bind:value={text}
      {placeholder}
      {maxlength}
      rows="1"
      class="block w-full resize-none overflow-hidden rounded-lg border border-neutral-200 bg-white px-3 py-2 text-base leading-[26px] placeholder:text-neutral-400"
      on:input={handleInput}
      on:blur={save}
    />
  {:else}
    <input
      type="text"
      bind:value={text}
      {placeholder}
      {maxlength}
      class="block w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-base leading-[26px] placeholder:text-neutral-400"
      on:input={handleInput}
      on:blur={save}
    />
  {/if}
  {#if saved}<p class="text-sm text-neutral-550">{savedText}</p>{/if}
</div>
